import { z } from 'zod';
import {
  assistantConfigurationSchema,
  AssistantError,
  communicationProfileSchema,
} from './assistant';
import {
  salesPlaybookSchema,
  salesTechniqueIdSchema,
} from './sales-psychology';
import { uuidV7Schema } from './ids';
import { adviserPoolSchema } from './advisers';
import {
  catalogConfigurationSchema,
  catalogAttributeSchema,
} from './catalog-contracts';

// Drafts preserve incomplete business text. Publication uses the complete runtime schema.
const text = z.string().max(600);
const item = z.object({ id: z.string().max(100), description: text }).strict();
const playbook = z
  .object({
    ...salesPlaybookSchema.shape,
    positioning: text,
    enabledTechniques: z.array(salesTechniqueIdSchema).max(8),
    motivations: z.array(item).max(12),
    concerns: z.array(item).max(12),
    benefits: z
      .array(
        z
          .object({
            id: z.string().max(100),
            motivationIds: z.array(z.string().max(100)).max(12),
            source: z.string().max(2000),
            evidence: text,
            meaning: text,
            kind: z.enum(['feature', 'social_proof', 'scarcity']),
          })
          .strict()
      )
      .max(30),
    adviserOutcomes: z
      .array(
        z
          .object({
            id: z.string().max(100),
            motivationIds: z.array(z.string().max(100)).max(12),
            concernIds: z.array(z.string().max(100)).max(12),
            description: text,
          })
          .strict()
      )
      .max(12),
  })
  .strict();
const calendar = z
  .object({
    serviceName: z.string().max(120).optional(),
    location: z.string().max(500).optional(),
    connectionId: z.string().max(100),
    calendarId: z.string().max(500),
    timezone: z.string().max(100),
    durationMinutes: z.number().min(0).max(240),
    appointmentKind: z
      .enum(['appointment', 'site_visit', 'sales_call'])
      .optional(),
    weeklyHours: z
      .array(
        z
          .object({
            day: z.number().int().min(0).max(6),
            opensAt: z.string().max(5),
            closesAt: z.string().max(5),
          })
          .strict()
      )
      .max(21),
  })
  .strict();
const pooledCalendar = calendar
  .omit({ connectionId: true, calendarId: true })
  .extend({
    pool: adviserPoolSchema.extend({
      members: z
        .array(
          z
            .object({
              adviserId: uuidV7Schema,
              bookingCalendarId: z.string().max(100),
            })
            .strict()
        )
        .max(50),
    }),
  })
  .strict();
export const configurationDraftSchema = z
  .object({
    ...assistantConfigurationSchema.shape,
    catalog: z
      .object({
        ...catalogConfigurationSchema.shape,
        label: z.string().max(80),
        attributes: z
          .array(
            z
              .object({
                ...catalogAttributeSchema.shape,
                key: z.string().max(80),
                label: z.string().max(100),
              })
              .strict()
          )
          .max(30),
      })
      .strict()
      .optional(),
    business: z
      .object({
        description: z.string().max(2000),
        audience: z.string().max(2000),
      })
      .strict()
      .transform((business) =>
        business.description.trim() || business.audience.trim()
          ? business
          : undefined
      )
      .optional(),
    profile: communicationProfileSchema.extend({
      assistantName: z.string().max(100),
      businessName: z.string().max(200),
    }),
    channels: assistantConfigurationSchema.shape.channels.extend({
      chat: assistantConfigurationSchema.shape.channels.shape.chat.extend({
        presentation: z
          .object({
            subtitle: z.string().max(200),
            introduction: z.string().max(1000),
            suggestions: z
              .array(
                z
                  .object({
                    id: z.string().max(100),
                    label: z.string().max(80),
                    message: z.string().max(1000),
                    requiredAction: z.enum([
                      'search_offerings',
                      'create_lead',
                      'calendar_availability',
                    ]),
                  })
                  .strict()
              )
              .max(3),
          })
          .strict()
          .optional(),
      }),
    }),
    capabilities: z.array(z.enum(['offerings', 'sales', 'collections'])).max(3),
    commercialOutcomes: z
      .array(z.enum(['qualified_lead', 'appointment']))
      .max(2)
      .optional(),
    salesPlaybook: playbook.optional(),
    calendar: z.union([
      calendar,
      pooledCalendar,
      assistantConfigurationSchema.shape.calendar,
    ]),
    appointmentServices: z
      .array(
        z
          .object({
            id: uuidV7Schema,
            name: z.string().max(120),
            location: z.string().max(500),
            calendar: z.union([
              calendar,
              pooledCalendar,
              assistantConfigurationSchema.shape.calendar.unwrap(),
            ]),
          })
          .strict()
      )
      .max(20)
      .optional(),
  })
  .strict();
export type ConfigurationDraft = z.infer<typeof configurationDraftSchema>;

export class ConfigurationValidationError extends AssistantError {
  constructor(readonly fields: string[]) {
    super('CONFIGURATION_INVALID', 400);
  }
}
export const storedConfigurationDraftSchema = z.object({
  revision: uuidV7Schema,
  baseReleaseId: uuidV7Schema.nullable(),
  configuration: configurationDraftSchema,
  updatedAt: z.string(),
});
export const saveConfigurationDraftSchema = z
  .object({
    expectedRevision: uuidV7Schema.nullable(),
    baseReleaseId: uuidV7Schema.nullable(),
    configuration: configurationDraftSchema,
  })
  .strict();
